import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import axios from "axios";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Checkbox,
  ListItemText,
  Select,
  LinearProgress,
  Alert,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FormatLineSpacingIcon from "@mui/icons-material/FormatLineSpacing";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import TablePagination from "@mui/material/TablePagination";

import {
  UITableShell,
  UITableToolbar,
} from "../../../../../../../components/ui";
import {
  getDisplayPreviewUrl,
  getOriginalFileUrl,
  getStoredPreviewUrl,
  isBrowserImageUrl,
  isPdfUrl,
} from "../../../../../../../utils/filePreview";
import {
  batchManifestEntries,
  consumeDirectoryInputFiles,
  createManifestFromScan,
  scanDirectoryInput,
  scanDropPayload,
  summarizeImportSelection,
} from "./importWorkflow";
import {
  buildFileRequestParams,
  getInitialSelectedFolder,
  isCustomerLibraryMode,
  resolveOrderFilesCustomerId,
  supportsOrderFileImportSessions,
} from "./orderFilesScope";
import {
  cancelOrderFileImportSession,
  completeOrderFileImportMultipartUpload,
  completeOrderFileImportSingleUpload,
  createOrderFileImportSession,
  finalizeOrderFileImportSession,
  failOrderFileImportItemUpload,
  getOrderFileImportItems,
  getOrderFileImportSession,
  getOrderFileImportMultipartPartUrl,
  initiateOrderFileImportItemUpload,
  listActiveCustomerFileImports,
  listActiveOrderFileImports,
  pauseOrderFileImportSession,
  prepareOrderFileImportSession,
  retryFailedOrderFileImportItems,
  resumeOrderFileImportSession,
  submitOrderFileImportManifestBatch,
  uploadOrderFileImportItem,
} from "../../../../../../../services/orderFileImports";
import apiClient from "../../../../../../../services/apiClient";
import { AuthContext } from "../../../../../../../helpers/AuthContext";
import { canDeleteCustomerFile } from "./fileDeletePermissions";
import {
  describeDirectStorageUploadError,
  getSignedSingleUploadContentType,
  runSingleUploadSequence,
  validateSingleUploadPlan,
} from "./storageUploadError";
import toast from "react-hot-toast";
import "./style.css";

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;
const PDF_EXT_RX = /\.pdf(?:$|[?#])/i;
const FILES_PER_PAGE_DEFAULT = 30;
const FILES_PER_PAGE_OPTIONS = [30, 60, 90, 120];
const IMPORT_FILE_CONCURRENCY = 3;
const IMPORT_MULTIPART_PART_CONCURRENCY = 4;
const IMPORT_PART_RETRY_LIMIT = 3;
const IMPORT_FILE_RETRY_LIMIT = 2;

const getExt = (url = "") => {
  const m = String(url).match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return m ? m[1].toUpperCase() : "FILE";
};

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const createIdempotencyKey = () => {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `order-file-import-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getUploadErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "The upload failed. Please try again.";

const formatFolderValue = (folder = null) =>
  folder?.relativePath || folder?.folderName || "general";

const isImportSessionActive = (status = "") =>
  ["scanning", "preparing", "ready", "uploading", "paused", "finalizing"].includes(
    String(status || "").toLowerCase(),
  );

const buildImportFileMatchKey = ({
  relativePath = "",
  size = 0,
  lastModified = 0,
}) =>
  [
    String(relativePath || "").trim().toLowerCase(),
    Number(size || 0),
    Number(lastModified || 0),
  ].join("::");



const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const FILE_COLUMNS = [
  { id: "s_no", label: "#", minWidth: 50, align: "center", visible: true },
  {
    id: "preview",
    label: "Preview",
    minWidth: 70,
    align: "center",
    visible: true,
  },
  {
    id: "name",
    label: "File Name",
    minWidth: 220,
    align: "left",
    visible: true,
  },
  { id: "ext", label: "Type", minWidth: 80, align: "center", visible: true },
  {
    id: "folder",
    label: "Folder",
    minWidth: 120,
    align: "left",
    visible: true,
  },
  { id: "tag", label: "Tag", minWidth: 110, align: "left", visible: true },
  {
    id: "status",
    label: "Status",
    minWidth: 100,
    align: "left",
    visible: false,
  },
  { id: "size", label: "Size", minWidth: 80, align: "right", visible: false },
  {
    id: "date",
    label: "Uploaded",
    minWidth: 110,
    align: "left",
    visible: true,
  },
  {
    id: "actions",
    label: "Actions",
    minWidth: 130,
    align: "center",
    visible: true,
    locked: true,
  },
];

const looksLikeImage = (value = "") => isBrowserImageUrl(String(value || ""));

const isPdfLike = (file = {}) => {
  const values = [
    file?.filePath,
    file?.url,
    file?.path,
    file?.Location,
    file?.previewUrl,
    file?.PreviewUrl,
    file?.thumbnailUrl,
    file?.fileName,
    file?.name,
    file?.mimeType,
    file?.contentType,
    file?.type,
  ];

  return values.some((value) => {
    const text = String(value || "").toLowerCase();
    return (
      text.endsWith(".pdf") ||
      text.includes("application/pdf") ||
      text === "pdf" ||
      PDF_EXT_RX.test(text)
    );
  });
};

const resolvePreviewImageSrc = (file = {}) =>
  getDisplayPreviewUrl(file) ||
  file?.thumbnail ||
  file?.previewImage ||
  file?.frontendImage ||
  file?.frontendUrl ||
  getStoredPreviewUrl(file) ||
  "";

const resolveOriginalFileSrc = (file = {}) =>
  getOriginalFileUrl(file) ||
  file?.path ||
  file?.Location ||
  "";

const resolvePdfSource = (file = {}) => {
  const candidates = [
    file?.filePath,
    file?.url,
    file?.path,
    file?.Location,
    file?.fileUrl,
    file?.FileUrl,
    file?.originalUrl,
    file?.sourceUrl,
    file?.downloadUrl,
    file?.previewUrl,
    file?.PreviewUrl,
    file?.frontendUrl,
  ];

  return (
    candidates.find((value) => {
      const text = String(value || "").trim();
      return text && /\.pdf(?:$|[?#])/i.test(text);
    }) || ""
  );
};

const hasOpenableSource = (file = {}) =>
  Boolean(resolveOriginalFileSrc(file) || resolvePreviewImageSrc(file));

const resolveBestFileSource = (file = {}) =>
  resolvePdfSource(file) ||
  resolveOriginalFileSrc(file) ||
  resolvePreviewImageSrc(file) ||
  "";

const pdfThumbCache = new Map();

const GenericPdfPlaceholder = ({ fileName = "PDF" }) => (
  <div className="file-thumbnail__image file-thumbnail__pdf-placeholder">
    <div className="file-thumbnail__pdf-page">
      <span className="file-thumbnail__pdf-label">PDF</span>
    </div>
    <div className="file-thumbnail__pdf-name">{fileName}</div>
  </div>
);

const PdfThumb = ({
  src,
  width = 320,
  height = 240,
  qualityDpr = 1.5,
  fallback = null,
}) => {
  const [dataUrl, setDataUrl] = useState(() =>
    src ? pdfThumbCache.get(`${src}::${width}x${height}::${qualityDpr}`) || "" : ""
  );

  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const cacheKey = src ? `${src}::${width}x${height}::${qualityDpr}` : "";
    setFailed(false);
    setDataUrl(cacheKey ? pdfThumbCache.get(cacheKey) || "" : "");
  }, [src, width, height, qualityDpr]);

  useEffect(() => {
    let cancelled = false;
    if (!src || dataUrl) return;

    const cacheKey = `${src}::${width}x${height}::${qualityDpr}`;

    (async () => {
      try {
        const loadingTask = getDocument({ url: src, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const baseViewport = page.getViewport({ scale: 1 });
        const fitScale = Math.min(width / baseViewport.width, height / baseViewport.height);
        const viewport = page.getViewport({ scale: Math.max(fitScale, 0.2) });

        const canvas = document.createElement("canvas");
        const dpr = Math.min(window.devicePixelRatio || 1, qualityDpr);
        const ctx = canvas.getContext("2d", {
          alpha: false,
          willReadFrequently: false,
        });

        if (!ctx) {
          throw new Error("Could not create canvas context for PDF thumbnail.");
        }

        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;

        const next = canvas.toDataURL("image/png");
        pdfThumbCache.set(cacheKey, next);
        setDataUrl(next);
      } catch (err) {
        console.warn("[OrderFiles PdfThumb] failed to render PDF thumbnail", {
          src,
          message: err?.message,
          name: err?.name,
        });
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, width, height, qualityDpr, dataUrl]);

  if (failed) return fallback;

  if (!dataUrl) {
    return <div className="file-thumbnail__pdf-loading">Loading preview…</div>;
  }

  return (
    <img
      src={dataUrl}
      alt="PDF preview"
      className="file-thumbnail__image"
      loading="eager"
      decoding="async"
      style={{
        objectFit: "contain",
      }}
    />
  );
};

const OrderFiles = ({
  order,
  store,
  customerId: explicitCustomerId = null,
  onSelectLogo,
  onSelectBanner,
}) => {
  const theme = useTheme();
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);

  const C = {
    headerBg: "#1e293b",
    headerText: "#f8fafc",
    headerMuted: "#94a3b8",
    border: theme.palette.divider,
    textPri: theme.palette.text.primary,
    textSec: theme.palette.text.secondary,
    textMuted: theme.palette.text.disabled,
    paperBg: theme.palette.background.paper,
    subtleBg: theme.palette.action.hover,
  };

  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxFile, setLightboxFile] = useState(null);

  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [metaTarget, setMetaTarget] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [metaForm, setMetaForm] = useState({
    tag: "",
    status: "",
    source: "",
    note: "",
    folder: "",
  });
  const [copyKey, setCopyKey] = useState("");

  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDesc, setNewFolderDesc] = useState("");
  const [folderCreating, setFolderCreating] = useState(false);
  const [folderCreateError, setFolderCreateError] = useState("");
  const [dragOverlayActive, setDragOverlayActive] = useState(false);
  const [importReviewOpen, setImportReviewOpen] = useState(false);
  const [importStarting, setImportStarting] = useState(false);
  const [importScanning, setImportScanning] = useState(false);
  const [importSelection, setImportSelection] = useState({ files: [], folders: [] });
  const [importOptions, setImportOptions] = useState({
    rootMode: "preserve",
    conflictMode: "keep_both",
  });
  const [importSummary, setImportSummary] = useState(null);
  const [importError, setImportError] = useState("");
  const [importSession, setImportSession] = useState(null);
  const [importItems, setImportItems] = useState([]);
  const [importMinimized, setImportMinimized] = useState(false);
  const [resumeBanner, setResumeBanner] = useState(null);
  const [importScanProgress, setImportScanProgress] = useState(null);
  const [sessionLimits, setSessionLimits] = useState(null);
  const [importActivity, setImportActivity] = useState([]);
  const [fileMapVersion, setFileMapVersion] = useState(0);

  // const [viewMode, setViewMode] = useState("table");
  const [viewMode, setViewMode] = useState("card");
  const [searchQuery, setSearchQuery] = useState("");
  const [density, setDensity] = useState("comfortable");
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [densityMenuAnchor, setDensityMenuAnchor] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const [moreFoldersAnchor, setMoreFoldersAnchor] = useState(null);
  const directoryInputRef = useRef(null);
  const fileMapRef = useRef(new Map());
  const importAbortRef = useRef(null);
  const dragDepthRef = useRef(0);
  const uploadAbortControllersRef = useRef(new Map());
  const importLoopActiveRef = useRef(false);
  const directoryInputModeRef = useRef("new");

  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(FILES_PER_PAGE_DEFAULT);

  const [visibleColumns, setVisibleColumns] = useState(
    () =>
      new Set(FILE_COLUMNS.filter((c) => c.visible !== false).map((c) => c.id)),
  );

  const configureDirectoryInput = useCallback((node) => {
    directoryInputRef.current = node;
    if (!node) return;
    // Set both attributes imperatively. This avoids React version-specific
    // handling of the non-standard, but broadly supported, picker attributes.
    node.setAttribute("webkitdirectory", "");
    node.setAttribute("directory", "");
  }, []);

  const densitySettings = {
    compact: { py: 0.75, fontSize: "0.8125rem" },
    comfortable: { py: 1.5, fontSize: "0.875rem" },
    spacious: { py: 2.25, fontSize: "0.9375rem" },
  };
  const currentDensity = densitySettings[density];

  const toggleColVisibility = (colId) =>
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      next.has(colId) ? next.delete(colId) : next.add(colId);
      return next;
    });

  const customerId = resolveOrderFilesCustomerId({
    customerId: explicitCustomerId,
    order,
    store,
  });
  const ownerReady = Boolean(customerId);
  const missingCustomerMessage = "No customer is attached to this PromoStore. Attach a customer in Overview to view customer files.";
  const foldersUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/files/customers/${customerId}/folders`;
  const filesUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/files/customers/${customerId}/files`;
  const activeOrderId = String(order?.OrderId || order?.id || "");
  const customerLibraryMode = isCustomerLibraryMode({
    customerId: explicitCustomerId,
    activeOrderId,
  });
  const [selectedFolder, setSelectedFolder] = useState(() =>
    getInitialSelectedFolder({
      customerLibraryMode,
    })
  );
  const supportsImportSessions = supportsOrderFileImportSessions({
    instanceUniqueId,
    activeOrderId,
    customerLibraryMode,
  });
  const importScopeType = customerLibraryMode ? "customer" : "order";
  const importStorageKey = useMemo(
    () =>
      `order-file-import.${instanceUniqueId || "unknown"}.${
        activeOrderId || `customer-${customerId || "unknown"}`
      }`,
    [instanceUniqueId, activeOrderId, customerId]
  );
  const destinationFolderRecord = useMemo(
    () =>
      folders.find((folder) => {
        const value = formatFolderValue(folder);
        return (
          String(value || "").toLowerCase() ===
          String(selectedFolder || "").toLowerCase()
        );
      }) || null,
    [folders, selectedFolder]
  );

  useEffect(() => {
    setSelectedFolder((current) => {
      const desired = getInitialSelectedFolder({ customerLibraryMode });
      if (!current) return desired;
      if (current === "general") return desired;
      return current;
    });
  }, [customerLibraryMode, activeOrderId]);
  const importProgress = useMemo(() => {
    if (!importSession) return { percent: 0, bytesPercent: 0 };
    const totalFiles = Number(importSession.totalFiles || 0);
    const totalBytes = Number(importSession.totalBytes || 0);
    return {
      percent: totalFiles ? Math.round((Number(importSession.uploadedFiles || 0) / totalFiles) * 100) : 0,
      bytesPercent: totalBytes ? Math.round((Number(importSession.uploadedBytes || 0) / totalBytes) * 100) : 0,
    };
  }, [importSession]);
  const importReadyItems = useMemo(
    () =>
      importItems.filter(
        (item) => item.entryType === "file" && ["ready", "failed"].includes(item.status)
      ),
    [importItems]
  );
  const importFailedItems = useMemo(
    () => importItems.filter((item) => item.entryType === "file" && item.status === "failed"),
    [importItems]
  );
  const importPendingFileAccess = useMemo(
    () =>
      importReadyItems.filter((item) => {
        const matchKey = buildImportFileMatchKey(item);
        return !fileMapRef.current.has(item.clientEntryId) && !fileMapRef.current.has(matchKey);
      }),
    [importReadyItems, fileMapVersion]
  );

  const filteredFiles = useMemo(() => {
    const matchingFiles = searchQuery.trim()
      ? files.filter(
          (f) =>
            (f.fileName || "")
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            (f.folder || "").toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : files;

    return [...matchingFiles].sort(
      (a, b) =>
        new Date(b.createdAt || b.uploadedAt || 0).getTime() -
        new Date(a.createdAt || a.uploadedAt || 0).getTime()
    );
  }, [files, searchQuery]);

  const paginationStart = tablePage * rowsPerPage;
  const paginationEnd = Math.min(paginationStart + rowsPerPage, filteredFiles.length);
  const paginatedFiles = filteredFiles.slice(paginationStart, paginationEnd);

  useEffect(() => {
    setTablePage(0);
  }, [searchQuery, selectedFolder, viewMode]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredFiles.length / rowsPerPage) - 1);
    if (tablePage > maxPage) {
      setTablePage(maxPage);
    }
  }, [filteredFiles.length, rowsPerPage, tablePage]);

  const fetchFolders = useCallback(async () => {
    if (!ownerReady) {
      setError(store && !order ? missingCustomerMessage : "Files need a customer before folders can load.");
      return;
    }
    setFoldersLoading(true);
    try {
      const res = await apiClient.get(foldersUrl, {
        params: {
          orderId: activeOrderId || undefined,
          instanceUniqueId: instanceUniqueId || undefined,
        },
      });
      setFolders(res.data.folders || []);
    } catch (err) {
      console.error("Error fetching folders", err);
      setError("Failed to fetch folders");
    } finally {
      setFoldersLoading(false);
    }
  }, [
    foldersUrl,
    ownerReady,
    order,
    store,
    missingCustomerMessage,
    activeOrderId,
    instanceUniqueId,
  ]);

  const fetchFiles = useCallback(async () => {
    if (!ownerReady) {
      setError(store && !order ? missingCustomerMessage : "Files need a customer before files can load.");
      return;
    }
    setFilesLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(filesUrl, {
        params: buildFileRequestParams({
          destinationFolderId: destinationFolderRecord?.id || null,
          selectedFolder,
          activeOrderId,
          instanceUniqueId,
        }),
      });
      setFiles(res.data.files || []);
    } catch (err) {
      console.error("Error fetching files", err);
      setError("Failed to fetch files");
    } finally {
      setFilesLoading(false);
    }
  }, [
    filesUrl,
    ownerReady,
    selectedFolder,
    order,
    store,
    missingCustomerMessage,
    destinationFolderRecord?.id,
    activeOrderId,
    instanceUniqueId,
  ]);

  useEffect(() => {
    if (ownerReady) {
      fetchFolders();
    } else {
      setFolders([]);
      setFiles([]);
      setError(store && !order ? missingCustomerMessage : "Files are unavailable until this record has a customer.");
    }
  }, [customerId, ownerReady, fetchFolders, order, store, missingCustomerMessage]);

  useEffect(() => {
    if (ownerReady) {
      fetchFiles();
    }
  }, [customerId, selectedFolder, ownerReady, fetchFiles]);

  const loadImportSession = useCallback(
    async (sessionId, options = {}) => {
      if (!sessionId) return null;
      try {
        const [sessionResponse, itemsResponse] = await Promise.all([
          getOrderFileImportSession(sessionId),
          getOrderFileImportItems(sessionId),
        ]);
        const nextSession = sessionResponse?.session || null;
        const nextItems = Array.isArray(itemsResponse?.items) ? itemsResponse.items : [];
        setImportSession(nextSession);
        setImportItems(nextItems);
        if (options.persist !== false && nextSession) {
          window.localStorage.setItem(importStorageKey, nextSession.id);
        }
        return nextSession;
      } catch (sessionError) {
        console.error("Failed to load import session", sessionError);
        if (options.clearOnError !== false) {
          window.localStorage.removeItem(importStorageKey);
        }
        setImportSession(null);
        setImportItems([]);
        return null;
      }
    },
    [importStorageKey]
  );

  const refreshActiveImportSessions = useCallback(async () => {
    if (!ownerReady || !supportsImportSessions) return;
    try {
      const response = customerLibraryMode
        ? await listActiveCustomerFileImports({
            instanceUniqueId,
            customerId,
          })
        : await listActiveOrderFileImports({
            instanceUniqueId,
            orderId: activeOrderId,
          });
      const sessions = Array.isArray(response?.sessions) ? response.sessions : [];
      setSessionLimits(response?.limits || null);
      const storedSessionId = window.localStorage.getItem(importStorageKey);
      const preferredSession =
        sessions.find((session) => session.id === storedSessionId) || sessions[0] || null;

      if (preferredSession) {
        setResumeBanner(preferredSession);
        if (!importSession || importSession.id !== preferredSession.id) {
          await loadImportSession(preferredSession.id, { persist: true });
        }
      } else {
        setResumeBanner(null);
        if (!isImportSessionActive(importSession?.status)) {
          setImportSession(null);
          setImportItems([]);
          window.localStorage.removeItem(importStorageKey);
        }
      }
    } catch (sessionError) {
      console.error("Failed to list active order file imports", sessionError);
    }
  }, [
    ownerReady,
    supportsImportSessions,
    instanceUniqueId,
    activeOrderId,
    customerLibraryMode,
    customerId,
    importStorageKey,
    importSession,
    loadImportSession,
  ]);

  useEffect(() => {
    if (!ownerReady || !supportsImportSessions) return undefined;
    refreshActiveImportSessions();
    const intervalId = window.setInterval(() => {
      refreshActiveImportSessions();
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [ownerReady, supportsImportSessions, refreshActiveImportSessions]);

  useEffect(() => {
    if (importSession?.id) {
      window.localStorage.setItem(importStorageKey, importSession.id);
      return;
    }
    window.localStorage.removeItem(importStorageKey);
  }, [importSession?.id, importStorageKey]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDragOverlayActive(false);
        dragDepthRef.current = 0;
        if (importScanning && importAbortRef.current) {
          importAbortRef.current.abort();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [importScanning]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setFolderCreateError("Folder name is required.");
      return;
    }
    if (!ownerReady) {
      setFolderCreateError(store && !order ? missingCustomerMessage : "Files need a customer before folders can be created.");
      return;
    }
    setFolderCreating(true);
    setFolderCreateError("");
    try {
      const res = await apiClient.post(
        foldersUrl,
        {
          folderName: newFolderName.trim(),
          description: newFolderDesc.trim(),
          instanceUniqueId: instanceUniqueId || null,
          orderId: activeOrderId || null,
          parentFolderId: destinationFolderRecord?.id || null,
          relativePath:
            (selectedFolder === "general" || selectedFolder === "all")
              ? newFolderName.trim()
              : [formatFolderValue(destinationFolderRecord), newFolderName.trim()]
                  .filter(Boolean)
                  .join("/"),
        },
      );
      const created = res.data.folder;

      setFolders((prev) => {
        const exists = prev.some(
          (f) =>
            String(f?.folderName || "").toLowerCase() ===
            String(created?.folderName || "").toLowerCase(),
        );
        return exists ? prev : [...prev, created];
      });

      await fetchFolders();
      setSelectedFolder(formatFolderValue(created));

      setNewFolderName("");
      setNewFolderDesc("");
      setFolderDialogOpen(false);
    } catch (err) {
      console.error("Error creating folder", err);
      setFolderCreateError("Failed to create folder. Please try again.");
    } finally {
      setFolderCreating(false);
    }
  };

  const openFolderDialog = () => {
    setFolderDialogOpen(true);
    setFolderCreateError("");
  };
  const closeFolderDialog = () => {
    setFolderDialogOpen(false);
    setNewFolderName("");
    setNewFolderDesc("");
    setFolderCreateError("");
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
  };

  const registerSelectionFiles = useCallback((entries = [], nextFileMap = new Map()) => {
    const currentMap = new Map();
    entries.forEach((entry) => {
      const file = nextFileMap.get(entry.clientEntryId);
      if (!file) return;
      currentMap.set(entry.clientEntryId, file);
      currentMap.set(buildImportFileMatchKey(entry), file);
    });
    fileMapRef.current = currentMap;
    setFileMapVersion((prev) => prev + 1);
  }, []);

  const beginImportSelection = useCallback(
    ({ files: scannedFiles = [], folders: scannedFolders = [], fileMap = new Map() } = {}) => {
      if (!scannedFiles.length && !scannedFolders.length) {
        setImportError("No supported files or folders were found in that selection.");
        toast.error("No supported files or folders were found to upload.");
        return;
      }
      registerSelectionFiles(scannedFiles, fileMap);
      const nextSelection = {
        files: scannedFiles,
        folders: scannedFolders,
      };
      setImportSelection(nextSelection);
      setImportSummary(summarizeImportSelection(nextSelection));
      setImportError("");
      setImportReviewOpen(true);
    },
    [registerSelectionFiles]
  );

  const handleScanFailure = useCallback((scanError) => {
    if (scanError?.name === "AbortError") {
      setImportError("Folder import scan was cancelled.");
      return;
    }
    console.error("Folder import scan failed", scanError);
    const message = scanError?.message || "Unable to scan the selected files and folders.";
    setImportError(message);
    toast.error(message);
  }, []);

  const runSelectionScan = useCallback(
    async (scanFn) => {
      if (!ownerReady) {
        setImportError(
          store && !order
            ? missingCustomerMessage
            : "Files need a customer before imports can proceed."
        );
        return;
      }

      const controller = new AbortController();
      importAbortRef.current = controller;
      setImportScanning(true);
      setImportScanProgress({ fileCount: 0, folderCount: 0 });
      setImportError("");

      try {
        const result = await scanFn({
          signal: controller.signal,
          onProgress: (progress) => setImportScanProgress(progress),
        });
        beginImportSelection(result);
      } catch (scanError) {
        handleScanFailure(scanError);
      } finally {
        setImportScanning(false);
        setDragOverlayActive(false);
        dragDepthRef.current = 0;
        importAbortRef.current = null;
      }
    },
    [
      ownerReady,
      store,
      order,
      missingCustomerMessage,
      beginImportSelection,
      handleScanFailure,
    ]
  );

  const handleImportFoldersClick = () => {
    directoryInputModeRef.current = "new";
    directoryInputRef.current?.click();
  };

  const handleResumeFolderSelection = () => {
    if (!importSession?.id) return;
    directoryInputModeRef.current = "resume";
    directoryInputRef.current?.click();
  };

  const handleDirectoryInputChange = async (event) => {
    const nextFiles = consumeDirectoryInputFiles(event.currentTarget);
    if (!nextFiles.length) {
      // A cancelled native picker does not represent an upload attempt. A
      // confirmed folder selection always provides at least one file here.
      return;
    }

    try {
      setImportScanning(true);
      setImportError("");
      const result = await scanDirectoryInput(nextFiles, {
        onProgress: (progress) => setImportScanProgress(progress),
      });

      if (directoryInputModeRef.current === "resume" && importSession?.id) {
        registerSelectionFiles(result.files, result.fileMap);
        setImportError("");
        const nextSession = await loadImportSession(importSession.id);
        if (nextSession) {
          await runImportUploadLoop(nextSession);
        }
        return;
      }

      beginImportSelection(result);
    } catch (scanError) {
      handleScanFailure(scanError);
    } finally {
      setImportScanning(false);
    }
  };

  const handleDragEnter = (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragOverlayActive(true);
  };

  const handleDragOver = (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setDragOverlayActive(false);
    }
  };

  const uploadProvidedFiles = useCallback(
    async (filesToUpload = [], { clearSelection = false } = {}) => {
      if (!filesToUpload.length) {
        setError("Choose a file before uploading.");
        return;
      }
      if (!ownerReady) {
        setError(
          store && !order
            ? missingCustomerMessage
            : "Files need a customer before uploads can proceed."
        );
        return;
      }

      setUploading(true);
      setError(null);
      const uploadLabel = filesToUpload.length === 1
        ? `Uploading ${filesToUpload[0].name}...`
        : `Uploading ${filesToUpload.length} files...`;
      const progressToastId = toast.loading(uploadLabel);
      const formData = new FormData();
      filesToUpload.forEach((file) => formData.append("file", file));
      formData.append(
        "folder",
        selectedFolder === "general"
          ? "general"
          : selectedFolder === "all"
          ? "unfiled"
          : formatFolderValue(destinationFolderRecord) || selectedFolder
      );
      formData.append("source", customerLibraryMode ? "customer-files" : "order-files");
      if (activeOrderId) {
        formData.append("orderId", activeOrderId);
      }
      if (instanceUniqueId) {
        formData.append("instanceUniqueId", instanceUniqueId);
      }
      if (destinationFolderRecord?.id) {
        formData.append("folderId", String(destinationFolderRecord.id));
      }

      try {
        const res = await apiClient.post(filesUrl, formData);
        const uploadedFiles = Array.isArray(res?.data?.files)
          ? res.data.files
          : res?.data?.file
            ? [res.data.file]
            : [];
        setFiles((prev) => [...prev, ...uploadedFiles]);
        if (clearSelection) {
          setSelectedFiles([]);
        }
        await fetchFiles();
        const uploadedCount = uploadedFiles.length || filesToUpload.length;
        toast.success(
          uploadedCount === 1
            ? `${filesToUpload[0].name} uploaded successfully.`
            : `${uploadedCount} files uploaded successfully.`,
          { id: progressToastId }
        );
      } catch (err) {
        console.error("Error uploading file", err);
        const message = getUploadErrorMessage(err);
        setError(`Upload failed: ${message}`);
        toast.error(`Upload failed: ${message}`, { id: progressToastId });
      } finally {
        setUploading(false);
      }
    },
    [
      ownerReady,
      store,
      order,
      missingCustomerMessage,
      selectedFolder,
      destinationFolderRecord,
      customerLibraryMode,
      activeOrderId,
      customerLibraryMode,
      instanceUniqueId,
      filesUrl,
      fetchFiles,
    ]
  );

  const handleDrop = async (event) => {
    if (!event.dataTransfer?.types?.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragOverlayActive(false);
    if (!supportsImportSessions) {
      const droppedFiles = Array.from(event.dataTransfer.files || []);
      if (!droppedFiles.length) {
        const message = "No files were found in the dropped item. Folder drag and drop is not supported on this screen.";
        setError(message);
        toast.error(message);
        return;
      }
      await uploadProvidedFiles(droppedFiles);
      return;
    }
    await runSelectionScan(({ signal, onProgress }) =>
      scanDropPayload({
        dataTransfer: event.dataTransfer,
        signal,
        onProgress,
      })
    );
  };

  const getPreviewStorageLabel = (file = {}) => {
    if (file?.previewStatus === "ready") return "Preview ready";
    if (file?.previewStatus === "failed") return "Preview failed";
    if (file?.previewStatus === "processing") return "Preview processing";
    return "";
  };

  const handleUpload = async () =>
    uploadProvidedFiles(selectedFiles, { clearSelection: true });

  const uploadBlobWithRetry = useCallback(async ({
    url,
    blob,
    contentType,
    signal,
  }) => {
    let attempt = 0;
    let lastError = null;
    while (attempt < IMPORT_PART_RETRY_LIMIT) {
      attempt += 1;
      try {
        return await axios.put(url, blob, {
          headers: {
            "Content-Type": contentType || "application/octet-stream",
          },
          signal,
        });
      } catch (uploadError) {
        lastError = uploadError;
        const storageFailure = describeDirectStorageUploadError(uploadError, url);
        console.error(
          "[order-file-import] Direct storage upload failed",
          storageFailure.diagnostic
        );
        uploadError.storageUserMessage = storageFailure.userMessage;
        if (signal?.aborted) throw uploadError;
        await sleep(Math.min(1500 * attempt, 4000));
      }
    }
    throw lastError || new Error("Upload failed.");
  }, []);

  const markItemActivity = useCallback((item, state, detail = "") => {
    setImportActivity((prev) => {
      const next = [
        {
          id: item.id,
          fileName: item.fileName,
          state,
          detail,
          updatedAt: Date.now(),
        },
        ...prev.filter((entry) => entry.id !== item.id),
      ];
      return next.slice(0, 6);
    });
  }, []);

  const processImportItemUpload = useCallback(
    async (session, item) => {
      const directFile =
        fileMapRef.current.get(item.clientEntryId) ||
        fileMapRef.current.get(buildImportFileMatchKey(item));

      if (!directFile) {
        setImportError(
          "This import needs the original local files again. Reselect the same folder set to continue."
        );
        setResumeBanner(session);
        return { halted: true, reason: "missing-file" };
      }

      let attempt = 0;
      let lastError = null;
      let failurePhase = "initiate";

      while (attempt <= IMPORT_FILE_RETRY_LIMIT) {
        attempt += 1;
        failurePhase = "initiate";
        try {
          markItemActivity(item, "Preparing upload");
          setImportItems((prev) =>
            prev.map((entry) =>
              entry.id === item.id ? { ...entry, status: "uploading" } : entry
            )
          );

          const backendUploadThreshold = Number(
            sessionLimits?.multipartThresholdBytes || 25 * 1024 * 1024
          );
          if (Number(directFile.size) < backendUploadThreshold) {
            failurePhase = "backend_upload";
            markItemActivity(item, "Uploading");
            await uploadOrderFileImportItem(session.id, item.id, directFile);
            markItemActivity(item, "Completed");
            await loadImportSession(session.id);
            return { completed: true };
          }

          const upload = await initiateOrderFileImportItemUpload(session.id, item.id);
          const uploadPlan = upload?.upload || upload;
          const controller = new AbortController();
          uploadAbortControllersRef.current.set(item.id, controller);

          if (uploadPlan.strategy === "single") {
            validateSingleUploadPlan(uploadPlan, item);
            console.info("[order-file-import] direct PUT starting", {
              sessionId: session.id,
              itemId: item.id,
              fileName: item.fileName,
              blobSize: directFile.size,
              blobType: directFile.type || null,
              signedContentType: uploadPlan.contentType,
              storageKey: uploadPlan.storageKey,
            });
            markItemActivity(item, "Uploading");
            try {
              await runSingleUploadSequence({
                uploadPlan,
                item,
                blob: directFile,
                put: ({ blob, uploadPlan: plan }) => uploadBlobWithRetry({
                  url: plan.uploadUrl,
                  blob,
                  // Content-Type participates in the single PUT signature.
                  contentType: getSignedSingleUploadContentType(plan),
                  signal: controller.signal,
                }),
                onPutComplete: () => console.info("[order-file-import] direct PUT complete", {
                  sessionId: session.id,
                  itemId: item.id,
                  fileName: item.fileName,
                }),
                complete: () => completeOrderFileImportSingleUpload(session.id, item.id),
              });
            } catch (completeError) {
              failurePhase = completeError.importFailurePhase || failurePhase;
              if (failurePhase === "complete_single") {
                console.error("[order-file-import] complete-single failed", {
                  sessionId: session.id,
                  itemId: item.id,
                  status: completeError?.response?.status || null,
                  message: completeError?.response?.data?.message || completeError?.message,
                });
              }
              throw completeError;
            }
          } else {
            const parts = [];
            const totalParts = Number(uploadPlan.totalParts || 0);
            const partSize = Number(uploadPlan.partSize || 0);
            let nextPart = 1;
            const worker = async () => {
              while (nextPart <= totalParts) {
                const partNumber = nextPart;
                nextPart += 1;
                const start = (partNumber - 1) * partSize;
                const end = Math.min(start + partSize, directFile.size);
                const chunk = directFile.slice(start, end);
                markItemActivity(
                  item,
                  "Uploading parts",
                  `Part ${partNumber} of ${totalParts}`
                );
                const partUrlResponse = await getOrderFileImportMultipartPartUrl(
                  session.id,
                  item.id,
                  partNumber
                );
                const partUrl = partUrlResponse?.part?.uploadUrl || partUrlResponse?.uploadUrl;
                const response = await uploadBlobWithRetry({
                  url: partUrl,
                  blob: chunk,
                  contentType: directFile.type || item.mimeType,
                  signal: controller.signal,
                });
                const etag =
                  response?.headers?.etag ||
                  response?.headers?.ETag ||
                  response?.headers?.Etag;
                if (!etag) {
                  throw new Error(`Upload part ${partNumber} did not return an ETag.`);
                }
                parts.push({
                  PartNumber: partNumber,
                  ETag: String(etag).replace(/^"+|"+$/g, ""),
                });
              }
            };

            await Promise.all(
              Array.from({
                length: Math.min(IMPORT_MULTIPART_PART_CONCURRENCY, totalParts || 1),
              }).map(() => worker())
            );

            parts.sort((a, b) => a.PartNumber - b.PartNumber);
            await completeOrderFileImportMultipartUpload(session.id, item.id, parts);
          }

          uploadAbortControllersRef.current.delete(item.id);
          markItemActivity(item, "Completed");
          await loadImportSession(session.id);
          return { completed: true };
        } catch (uploadError) {
          lastError = uploadError;
          uploadAbortControllersRef.current.delete(item.id);
          if (uploadError?.name === "CanceledError" || uploadError?.code === "ERR_CANCELED") {
            return { cancelled: true };
          }
          // The storage PUT already has its own bounded retry loop. Once the
          // server has issued a plan the item remains `uploading`, so starting
          // a new initiation would only replace the real PUT/completion error
          // with "This import item is not ready for upload."
          const responseStatus = Number(uploadError?.response?.status || 0);
          const retryableBackendFailure = failurePhase === "backend_upload" && (
            !responseStatus || responseStatus >= 500
          );
          if (
            (failurePhase === "initiate" || retryableBackendFailure) &&
            attempt <= IMPORT_FILE_RETRY_LIMIT
          ) {
            markItemActivity(item, "Retrying", `Attempt ${attempt + 1}`);
            await sleep(Math.min(1000 * attempt, 3000));
            continue;
          }
          break;
        }
      }

      const safeMessage =
        lastError?.storageUserMessage ||
        lastError?.response?.data?.message ||
        lastError?.message ||
        "Upload failed.";
      await failOrderFileImportItemUpload(session.id, item.id, {
        errorCode: failurePhase === "initiate"
          ? "STORAGE_UPLOAD_INIT_FAILED"
          : failurePhase === "backend_upload"
            ? "STORAGE_BACKEND_UPLOAD_FAILED"
          : failurePhase === "complete_single"
            ? "STORAGE_UPLOAD_COMPLETE_FAILED"
            : "STORAGE_DIRECT_PUT_FAILED",
        errorMessage: safeMessage,
        failurePhase,
      }).catch((failError) => {
        console.error("Failed to mark import item upload failure", failError);
      });
      markItemActivity(item, "Failed", safeMessage);
      await loadImportSession(session.id);
      return { failed: true };
    },
    [loadImportSession, markItemActivity, sessionLimits?.multipartThresholdBytes, uploadBlobWithRetry]
  );

  const runImportUploadLoop = useCallback(
    async (sessionOverride = null) => {
      const activeSession = sessionOverride || importSession;
      if (!activeSession?.id || importLoopActiveRef.current) return;

      importLoopActiveRef.current = true;

      try {
        let haltedForMissingFiles = false;
        while (!haltedForMissingFiles) {
          const freshSession = await loadImportSession(activeSession.id);
          if (!freshSession) break;
          if (["paused", "cancelled", "completed", "completed_with_errors"].includes(freshSession.status)) {
            break;
          }

          const readyItemsResponse = await getOrderFileImportItems(freshSession.id);
          const readyItems = Array.isArray(readyItemsResponse?.items)
            ? readyItemsResponse.items.filter(
                (item) => item.entryType === "file" && item.status === "ready"
              )
            : [];

          if (!readyItems.length) {
            const latestItems = Array.isArray(readyItemsResponse?.items) ? readyItemsResponse.items : [];
            const pendingUploads = latestItems.some((item) =>
              ["ready", "uploading"].includes(item.status)
            );
            if (!pendingUploads) {
              await finalizeOrderFileImportSession(freshSession.id);
              await loadImportSession(freshSession.id);
              await fetchFolders();
              await fetchFiles();
            }
            break;
          }

          const batch = readyItems.slice(0, IMPORT_FILE_CONCURRENCY);
          const outcomes = await Promise.all(batch.map((item) => processImportItemUpload(freshSession, item)));
          if (outcomes.some((outcome) => outcome?.reason === "missing-file")) {
            haltedForMissingFiles = true;
          }
        }
      } finally {
        importLoopActiveRef.current = false;
      }
    },
    [fetchFiles, fetchFolders, importSession, loadImportSession, processImportItemUpload]
  );

  const handleStartImport = async () => {
    if (importStarting) return;
    if (!supportsImportSessions) {
      setImportError("The current files view does not have a valid import scope.");
      return;
    }
    if (!importSelection.files.length && !importSelection.folders.length) {
      setImportError("Choose files or folders to import first.");
      return;
    }

    setImportError("");
    setImportStarting(true);
    try {
      const created = await createOrderFileImportSession({
        instanceUniqueId,
        orderId: activeOrderId,
        customerId: customerLibraryMode ? customerId : null,
        scopeType: customerLibraryMode ? "customer" : "order",
        destinationFolderId: destinationFolderRecord?.id || null,
        rootMode: importOptions.rootMode,
        conflictMode: importOptions.conflictMode,
        idempotencyKey: createIdempotencyKey(),
      });
      const session = created?.session;
      if (!session?.id) {
        throw new Error("Import session could not be created.");
      }

      const manifestEntries = createManifestFromScan(importSelection);
      const batches = batchManifestEntries(manifestEntries, sessionLimits?.manifestBatchLimit || 400);
      for (const batch of batches) {
        await submitOrderFileImportManifestBatch(session.id, batch);
      }

      await prepareOrderFileImportSession(session.id);
      await fetchFolders();
      const loaded = await loadImportSession(session.id);
      setResumeBanner(loaded || session);
      setImportReviewOpen(false);
      setImportMinimized(false);
      await runImportUploadLoop(loaded || session);
    } catch (startError) {
      console.error("Failed to start order file import", startError);
      const message = getUploadErrorMessage(startError) || "Unable to start the import.";
      setImportError(message);
      toast.error(message);
    } finally {
      setImportStarting(false);
    }
  };

  const handlePauseImport = async () => {
    if (!importSession?.id) return;
    await pauseOrderFileImportSession(importSession.id);
    await loadImportSession(importSession.id);
  };

  const handleResumeImport = async () => {
    if (!importSession?.id) return;
    await resumeOrderFileImportSession(importSession.id);
    const nextSession = await loadImportSession(importSession.id);
    if (importPendingFileAccess.length) {
      setResumeBanner(nextSession || importSession);
      setImportError("Reselect the original folder set to continue the paused import.");
      return;
    }
    await runImportUploadLoop(nextSession || importSession);
  };

  const handleCancelImport = async () => {
    if (!importSession?.id) return;
    uploadAbortControllersRef.current.forEach((controller) => controller.abort());
    uploadAbortControllersRef.current.clear();
    await cancelOrderFileImportSession(importSession.id);
    await loadImportSession(importSession.id);
  };

  const handleRetryFailedItems = async () => {
    if (!importSession?.id || !importFailedItems.length) return;
    await retryFailedOrderFileImportItems(importSession.id);
    const nextSession = await loadImportSession(importSession.id);
    if (importPendingFileAccess.length) {
      setImportError("Reselect the original folder set to retry the failed files.");
      return;
    }
    await runImportUploadLoop(nextSession || importSession);
  };

  const handleFinalizeImport = async () => {
    if (!importSession?.id) return;
    await finalizeOrderFileImportSession(importSession.id);
    await loadImportSession(importSession.id);
    await fetchFolders();
    await fetchFiles();
  };

  const openLightbox = (file) => {
    setLightboxFile(file);
    setLightboxOpen(true);
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxFile(null);
  };

  const handleSetAsLogo = async (file) => {
    if (!store) return;
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/update-logo/${store.id}`,
        { logoFileId: file.id },
      );
      if (onSelectLogo) onSelectLogo(file, res.data?.promostore);
      alert("✅ Site logo updated!");
    } catch {
      alert("Failed to set site logo");
    }
  };

  const handleSetAsBanner = async (file) => {
    if (!store) return;
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/update-banner/${store.id}`,
        { bannerFileId: file.id },
      );
      if (onSelectBanner) onSelectBanner(file, res.data?.promostore);
      alert("✅ Site banner updated!");
    } catch {
      alert("Failed to set site banner");
    }
  };

  const openMetaDialog = (file) => {
    setMetaTarget(file);
    setMetaForm({
      tag: "",
      status: "",
      source: "",
      note: "",
      folder: file?.folder || "general",
    });
    setMetaDialogOpen(true);
  };
  const closeMetaDialog = () => {
    if (deletingFile) return;
    setMetaDialogOpen(false);
    setMetaTarget(null);
    setDeleteConfirmOpen(false);
  };

  const handleDeleteFile = async () => {
    if (!metaTarget || !customerId || deletingFile) return;
    setDeletingFile(true);
    try {
      await apiClient.delete(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/files/customers/${customerId}/files/${metaTarget.id}`,
        { params: { instanceUniqueId } }
      );
      setFiles((current) => current.filter((file) => file.id !== metaTarget.id));
      setSelectedFiles((current) => current.filter((file) => file.id !== metaTarget.id));
      setDeleteConfirmOpen(false);
      setMetaDialogOpen(false);
      setMetaTarget(null);
      toast.success("File deleted successfully.");
    } catch (deleteError) {
      toast.error(
        deleteError?.response?.data?.message || "Unable to delete this file."
      );
    } finally {
      setDeletingFile(false);
    }
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setMetaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveMeta = async () => {
    if (!metaTarget || !customerId) {
      setError(store && !order ? missingCustomerMessage : "Missing customer for metadata editing.");
      return;
    }
    try {
      const targetFolderRecord =
        folders.find(
          (folder) =>
            String(formatFolderValue(folder)).toLowerCase() ===
            String(metaForm.folder || "").toLowerCase()
        ) || null;
      if (metaForm.tag) {
          await apiClient.post(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/files/customers/${customerId}/files/${metaTarget.id}/meta`,
            {
              tag: metaForm.tag,
            status: metaForm.status || null,
            source: metaForm.source || null,
            meta: metaForm.note ? { note: metaForm.note } : null,
          },
        );
      }
      if (metaForm.folder !== metaTarget.folder) {
          await apiClient.patch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/files/customers/${customerId}/files/${metaTarget.id}`,
            {
              folder: metaForm.folder || "general",
            folderId: targetFolderRecord?.id || null,
          },
        );
      }
      await fetchFiles();
      closeMetaDialog();
    } catch {
      setError("Failed to save file metadata.");
    }
  };

  const handleCopyLink = async (file) => {
    try {
      const src = resolveBestFileSource(file);
      if (!src) return;
      await navigator.clipboard?.writeText(src);
      setCopyKey(String(file.id));
      window.setTimeout(() => setCopyKey(""), 1500);
    } catch {}
  };
  const openFile = (file) => {
    const openSrc = resolveBestFileSource(file);
    if (openSrc) openLightbox({ ...file, filePath: openSrc });
    else openMetaDialog(file);
  };

  const resolvePrimaryMeta = (file) => {
    const meta = Array.isArray(file?.meta) ? file.meta : [];
    if (!meta.length) return null;
    return [...meta].sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime(),
    )[0];
  };

  const getExportRows = () =>
    filteredFiles.map((f, i) => {
      const m = resolvePrimaryMeta(f);
      return {
        "#": i + 1,
        "File Name": f.fileName || "",
        Type: getExt(f.filePath || ""),
        Folder: f.folder || "general",
        Tag: m?.tag || "",
        Status: m?.status || "",
        Uploaded: formatDate(f.createdAt),
      };
    });

  const handleExportCSV = () => {
    if (!filteredFiles.length) return;
    const rows = getExportRows();
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers
          .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "order-files.csv";
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuAnchor(null);
  };

  const handleExportPDF = () => {
    if (!filteredFiles.length) return;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Order Files", 14, 15);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    const rows = getExportRows();
    autoTable(doc, {
      head: [Object.keys(rows[0])],
      body: rows.map((r) => Object.values(r)),
      startY: 28,
      styles: { fontSize: 7, cellPadding: 1.5, overflow: "ellipsize" },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save("order-files.pdf");
    setExportMenuAnchor(null);
  };

const renderThumbCell = (file, size = 40) => {
  const previewSrc = resolvePreviewImageSrc(file);
  const originalSrc = resolveOriginalFileSrc(file);
  const pdfSrc = resolvePdfSource(file);
  const openSrc = pdfSrc || originalSrc || previewSrc;
  const isPdf = isPdfUrl(openSrc) || isPdfLike(file);
  const imageSrc = previewSrc || originalSrc;
  const isImg = looksLikeImage(previewSrc) || looksLikeImage(originalSrc);
  const fileName = file.fileName || file.name || "File";

  return (
    <Box
      onClick={() => openFile({ ...file, filePath: openSrc })}
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        border: `1px solid ${C.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f8fafc",
        overflow: "hidden",
        cursor: openSrc ? "pointer" : "default",
        flexShrink: 0,
      }}
    >
      {isImg && imageSrc ? (
        <Box
          component="img"
          src={imageSrc}
          alt={fileName}
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : isPdf && openSrc ? (
        <PdfThumb
          src={openSrc}
          width={size}
          height={size}
          fallback={<GenericPdfPlaceholder fileName={fileName} />}
        />
      ) : (
        <InsertDriveFileIcon
          sx={{ fontSize: size * 0.5, color: C.textMuted }}
        />
      )}
    </Box>
  );
};

  const renderSkeletonTable = () => (
    <TableContainer sx={{ flex: 1, overflow: "auto" }}>
      <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {FILE_COLUMNS.filter((c) => visibleColumns.has(c.id)).map((col) => (
              <TableCell
                key={col.id}
                sx={{
                  minWidth: col.minWidth,
                  px: 2,
                  py: 1.25,
                  backgroundColor: `${C.headerBg} !important`,
                  color: `${C.headerText} !important`,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  borderBottom: "none",
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(6)].map((_, i) => (
            <TableRow key={i}>
              {FILE_COLUMNS.filter((c) => visibleColumns.has(c.id)).map(
                (col) => (
                  <TableCell key={col.id} sx={{ px: 2, py: currentDensity.py }}>
                    <Skeleton
                      variant="rectangular"
                      height={18}
                      sx={{ borderRadius: 1, opacity: 1 - i * 0.1 }}
                    />
                  </TableCell>
                ),
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderSkeletonCards = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 2,
        p: 2,
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            border: `1px solid ${C.border}`,
            borderRadius: "10px",
            overflow: "hidden",
            bgcolor: C.paperBg,
          }}
        >
          <Skeleton variant="rectangular" height={150} />
          <Box sx={{ p: 1.25 }}>
            <Skeleton width="80%" height={16} sx={{ mb: 0.75 }} />
            <Skeleton width="50%" height={12} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        color: C.textMuted,
      }}
    >
      <FolderOpenIcon sx={{ fontSize: 52, mb: 1.5, opacity: 0.3 }} />
      <Typography
        variant="body1"
        fontWeight={600}
        sx={{ mb: 0.5, color: C.textSec }}
      >
        {searchQuery ? "No matching files" : "No files uploaded yet"}
      </Typography>
      <Typography variant="body2" sx={{ color: C.textMuted }}>
        {searchQuery
          ? "Try a different search term."
          : "Upload a file using the Upload button above."}
      </Typography>
    </Box>
  );

  const renderTableView = () => (
    <TableContainer
      sx={{
        flex: 1,
        overflow: "auto",
        // minHeight: 0,
        minHeight: 420,
        maxHeight: 520,
        "&::-webkit-scrollbar": { width: 6, height: 6 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: C.border,
          borderRadius: 99,
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {FILE_COLUMNS.filter((c) => visibleColumns.has(c.id)).map((col) => (
              <TableCell
                key={col.id}
                align={col.align}
                sx={{
                  minWidth: col.minWidth,
                  px: 2,
                  py: 1.25,
                  backgroundColor: `${C.headerBg} !important`,
                  color: `${C.headerText} !important`,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            "& .MuiTableCell-root": { borderBottom: `1px solid ${C.border}` },
            "& .MuiTableRow-root:last-child .MuiTableCell-root": {
              borderBottom: "none",
            },
          }}
        >
          {/* {filteredFiles.map((file, idx) => { */}
          {paginatedFiles.map((file, idx) => {
            const primaryMeta = resolvePrimaryMeta(file);
            const ext = getExt(resolveBestFileSource(file));
            return (
              <TableRow
                key={file.id}
                hover
                sx={{
                  cursor: "default",
                  // bgcolor: idx % 2 === 0 ? C.paperBg : C.subtleBg,
                  bgcolor: idx % 2 === 0 ? C.paperBg : "#f8fafc",
                  transition: "background-color 0.12s",
                }}
              >
                {visibleColumns.has("s_no") && (
                  <TableCell
                    align="center"
                    sx={{ py: currentDensity.py, px: 2 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: C.textMuted, fontWeight: 600 }}
                    >
                      {tablePage * rowsPerPage + idx + 1}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns.has("preview") && (
                  <TableCell
                    align="center"
                    sx={{ py: currentDensity.py, px: 1.5 }}
                  >
                    {renderThumbCell(file, 40)}
                  </TableCell>
                )}

                {visibleColumns.has("name") && (
                  <TableCell sx={{ py: currentDensity.py, px: 2 }}>
                    <Tooltip
                      title={file.fileName || "Untitled"}
                      placement="top-start"
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color: C.textPri,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 260,
                          fontSize: currentDensity.fontSize,
                        }}
                      >
                        {file.fileName || "Untitled"}
                      </Typography>
                    </Tooltip>
                    {getPreviewStorageLabel(file) ? (
                      <Typography
                        variant="caption"
                        sx={{ color: C.textMuted, display: "block", mt: 0.25 }}
                      >
                        {getPreviewStorageLabel(file)}
                      </Typography>
                    ) : null}
                  </TableCell>
                )}

                {visibleColumns.has("ext") && (
                  <TableCell
                    align="center"
                    sx={{ py: currentDensity.py, px: 2 }}
                  >
                    <Chip
                      label={ext}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        bgcolor: C.subtleBg,
                        color: C.textSec,
                        border: `1px solid ${C.border}`,
                        "& .MuiChip-label": { px: "8px" },
                      }}
                    />
                  </TableCell>
                )}

                {visibleColumns.has("folder") && (
                  <TableCell sx={{ py: currentDensity.py, px: 2 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <FolderOpenIcon
                        sx={{ fontSize: 13, color: C.textMuted }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: C.textSec,
                          fontSize: currentDensity.fontSize,
                        }}
                      >
                        {file.folder || "general"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                {visibleColumns.has("tag") && (
                  <TableCell sx={{ py: currentDensity.py, px: 2 }}>
                    {primaryMeta?.tag ? (
                      // <Chip
                      //   label={primaryMeta.tag}
                      //   size="small"
                      //   color="info"
                      //   sx={{
                      //     height: 20,
                      //     fontSize: "0.65rem",
                      //     fontWeight: 600,
                      //     "& .MuiChip-label": { px: "8px" },
                      //   }}
                      // />
                      <Chip
                        label={primaryMeta.tag}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          bgcolor: "error.main",
                          color: "#fff",
                          "& .MuiChip-label": { px: "8px", color: "#fff" },
                        }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: C.textMuted }}>
                        —
                      </Typography>
                    )}
                  </TableCell>
                )}

                {visibleColumns.has("status") && (
                  <TableCell sx={{ py: currentDensity.py, px: 2 }}>
                    {primaryMeta?.status ? (
                      <Chip
                        label={primaryMeta.status}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          bgcolor: C.subtleBg,
                          color: C.textSec,
                          border: `1px solid ${C.border}`,
                          "& .MuiChip-label": { px: "8px" },
                        }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: C.textMuted }}>
                        —
                      </Typography>
                    )}
                  </TableCell>
                )}

                {visibleColumns.has("size") && (
                  <TableCell
                    align="right"
                    sx={{ py: currentDensity.py, px: 2 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: C.textSec,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      {formatBytes(file.size || file.fileSize)}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns.has("date") && (
                  <TableCell sx={{ py: currentDensity.py, px: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: C.textSec, fontSize: "0.78rem" }}
                    >
                      {formatDate(file.createdAt || file.uploadedAt)}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns.has("actions") && (
                  <TableCell
                    align="center"
                    sx={{ py: currentDensity.py, px: 1.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                    >
                      <Tooltip
                        title={
                          copyKey === String(file.id) ? "Copied!" : "Copy link"
                        }
                        arrow
                      >
<IconButton
  size="small"
  onClick={(e) => {
    e.stopPropagation();
    handleCopyLink(file);
  }}
  sx={{ color: C.textMuted }}
>
  <ContentCopyIcon sx={{ fontSize: 15 }} />
</IconButton>

                      </Tooltip>
                      <Tooltip title="Preview" arrow>
<IconButton
  size="small"
  onClick={(e) => {
    e.stopPropagation();
    openFile(file);
  }}
  sx={{ color: C.textMuted }}
>
  <OpenInNewIcon sx={{ fontSize: 15 }} />
</IconButton>
                      </Tooltip>
                      <Tooltip title="Edit details" arrow>
<IconButton
  size="small"
  onClick={(e) => {
    e.stopPropagation();
    openMetaDialog(file);
  }}
  sx={{ color: C.textMuted }}
>
  <EditIcon sx={{ fontSize: 15 }} />
</IconButton>
                      </Tooltip>
                      {store && (
                        <>
                          <Tooltip title="Set as Logo" arrow>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSetAsLogo(file)}
                              sx={{
                                fontSize: "0.65rem",
                                height: 24,
                                textTransform: "none",
                                px: 1,
                                fontWeight: 600,
                                borderRadius: "6px",
                              }}
                            >
                              Logo
                            </Button>
                          </Tooltip>
                          <Tooltip title="Set as Banner" arrow>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSetAsBanner(file)}
                              sx={{
                                fontSize: "0.65rem",
                                height: 24,
                                textTransform: "none",
                                px: 1,
                                fontWeight: 600,
                                borderRadius: "6px",
                              }}
                            >
                              Banner
                            </Button>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCardGrid = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "0 0 auto",
        minHeight: "auto",
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          overflow: "visible",
          p: 2,
          backgroundColor: "#f1f5f9",
          minHeight: "auto",
        }}
      >
        {filteredFiles.length === 0 ? (
          renderEmptyState()
        ) : (
          <Box
            sx={{
              display: "grid",
              // Keep cards wide enough for thumbnails, badges, metadata, and actions.
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 2,
              gridAutoRows: "auto",
              alignItems: "start",
            }}
          >
            {paginatedFiles.map((file) => {
              const primaryMeta = resolvePrimaryMeta(file);
              const previewSrc = resolvePreviewImageSrc(file);
              const originalSrc = resolveOriginalFileSrc(file);
              const pdfSrc = resolvePdfSource(file);
              const openSrc = pdfSrc || originalSrc || previewSrc;
              const isPdf = isPdfUrl(openSrc) || isPdfLike(file);
              const imageSrc = previewSrc || originalSrc;
              const isImg = looksLikeImage(previewSrc) || looksLikeImage(originalSrc);
              const ext = getExt(openSrc || originalSrc || previewSrc);
              return (
                <Box
                  key={file.id}
                  sx={{
                    bgcolor: C.paperBg,
                    borderRadius: "12px",
                    border: `1px solid ${C.border}`,
                    position: "relative",
                    overflow: "visible",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 268,
                    height: "auto",
                    alignSelf: "start",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    transition: "box-shadow 0.15s ease",
                    "&:hover": {
                      boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
                    },
                  }}
                >
                  <Box
                    onClick={() => openFile({ ...file, filePath: openSrc })}
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: 150,
                      bgcolor: "#f8fafc",
                      cursor: "pointer",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderBottom: `1px solid ${C.border}`,
                      flexShrink: 0,
                    }}
                  >
                    {isImg && imageSrc ? (
                      <>
                        <Box
                          component="img"
                          src={imageSrc}
                          alt={file.fileName}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            p: 1,
                            transition: "opacity 0.15s ease",
                            "&:hover": { opacity: 0.95 },
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display =
                                "flex";
                            }
                          }}
                        />
                        {/* Fallback if image 404s */}
                        <Box
                          sx={{
                            display: "none",
                            flexDirection: "column",
                            alignItems: "center",
                            color: C.textMuted,
                          }}
                        >
                          <InsertDriveFileIcon
                            sx={{ fontSize: 32, mb: 0.5, opacity: 0.4 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            {ext}
                          </Typography>
                        </Box>
                      </>
) : isPdf && openSrc ? (
  <PdfThumb
    src={openSrc}
    width={180}
    height={140}
    fallback={<GenericPdfPlaceholder fileName={file.fileName || file.name || "PDF"} />}
  />
) : (
                      /* Non-image fallback */
                      <Box sx={{ textAlign: "center", color: C.textMuted }}>
                        <InsertDriveFileIcon
                          sx={{ fontSize: 36, opacity: 0.35, mb: 0.5 }}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={700}
                          sx={{
                            letterSpacing: "0.08em",
                            color: C.textMuted,
                            fontSize: "0.68rem",
                          }}
                        >
                          {ext}
                        </Typography>
                      </Box>
                    )}

                    <Chip
                      label={ext}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 7,
                        left: 7,
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        bgcolor: "rgba(15,23,42,0.65)",
                        color: "#fff",
                        backdropFilter: "blur(4px)",
                        "& .MuiChip-label": { px: "6px", color: "#fff" },
                      }}
                    />

                    {primaryMeta?.tag && (
                      <Chip
                        label={primaryMeta.tag}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 7,
                          right: 7,
                          height: 18,
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          bgcolor: "error.main",
                          "& .MuiChip-label": { px: "6px", color: "#fff" },
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      px: 1.25,
                      pt: 1,
                      pb: 0.75,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.4,
                    }}
                  >
                    <Tooltip
                      title={file.fileName || "Untitled"}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color: C.textPri,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.8125rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {file.fileName || "Untitled"}
                      </Typography>
                    </Tooltip>
                    {getPreviewStorageLabel(file) ? (
                      <Typography
                        variant="caption"
                        sx={{ color: C.textMuted, fontSize: "0.68rem" }}
                      >
                        {getPreviewStorageLabel(file)}
                      </Typography>
                    ) : null}

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={0.4}>
                        <FolderOpenIcon
                          sx={{ fontSize: 11, color: C.textMuted }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: C.textMuted, fontSize: "0.7rem" }}
                        >
                          {file.folder || "general"}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ color: C.textMuted, fontSize: "0.7rem" }}
                      >
                        {formatDate(file.createdAt || file.uploadedAt)}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.25,
                      px: 0.75,
                      py: 0.5,
                      borderTop: `1px solid ${C.border}`,
                      bgcolor: "#f8fafc",
                      flexShrink: 0,
                      minHeight: 38,
                    }}
                  >
                    <Tooltip
                      title={
                        copyKey === String(file.id) ? "Copied!" : "Copy link"
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopyLink(file)}
                        sx={{
                          color: C.textMuted,
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Preview" arrow>
                      <IconButton
                        size="small"
                        onClick={() => openLightbox(file)}
                        sx={{
                          color: C.textMuted,
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit details" arrow>
                      <IconButton
                        size="small"
                        onClick={() => openMetaDialog(file)}
                        sx={{
                          color: C.textMuted,
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {store && (
                      <>
                        <Tooltip title="Set as Logo" arrow>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSetAsLogo(file)}
                            sx={{
                              fontSize: "0.6rem",
                              height: 20,
                              textTransform: "none",
                              px: 0.75,
                              fontWeight: 600,
                              borderRadius: "5px",
                              minWidth: 0,
                            }}
                          >
                            Logo
                          </Button>
                        </Tooltip>
                        <Tooltip title="Set as Banner" arrow>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSetAsBanner(file)}
                            sx={{
                              fontSize: "0.6rem",
                              height: 20,
                              textTransform: "none",
                              px: 0.75,
                              fontWeight: 600,
                              borderRadius: "5px",
                              minWidth: 0,
                            }}
                          >
                            Banner
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: `1px solid ${C.border}`,
          bgcolor: C.paperBg,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" sx={{ color: C.textMuted }}>
          {filteredFiles.length > 0 ? (
            <>
              {filteredFiles.length} file{filteredFiles.length !== 1 ? "s" : ""}{" "}
              in{" "}
              <strong>
                {selectedFolder === "general"
                  ? "General Inbox"
                  : selectedFolder === "all"
                  ? "All Files"
                  : destinationFolderRecord?.relativePath ||
                    destinationFolderRecord?.folderName ||
                    selectedFolder}
              </strong>
            </>
          ) : (
            "No files"
          )}
        </Typography>
        {searchQuery && (
          <Typography
            variant="caption"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            Filtered: "{searchQuery}"
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      className="order-files-root"
      sx={{ display: "flex", flexDirection: "column", minHeight: 0, position: "relative" }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {supportsImportSessions && (
        <input
          ref={configureDirectoryInput}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleDirectoryInputChange}
        />
      )}

      {supportsImportSessions && resumeBanner && isImportSessionActive(resumeBanner.status) && (
        <Alert
          severity="info"
          sx={{ mb: 1.25, alignItems: "center" }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                onClick={() => loadImportSession(resumeBanner.id)}
              >
                View
              </Button>
              <Button color="inherit" size="small" onClick={handleResumeFolderSelection}>
                Reselect folders
              </Button>
            </Stack>
          }
        >
          Import in progress for this order: {resumeBanner.uploadedFiles || 0} of{" "}
          {resumeBanner.totalFiles || 0} files completed.
        </Alert>
      )}

      {supportsImportSessions && (importScanning || importSession?.id) && !importMinimized && (
        <Box
          sx={{
            mb: 1.25,
            border: `1px solid ${C.border}`,
            borderRadius: "10px",
            bgcolor: "background.paper",
            p: 1.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {importScanning ? "Scanning import selection" : "Folder import progress"}
              </Typography>
              <Typography variant="caption" sx={{ color: C.textMuted, display: "block", mb: 1 }}>
                {importScanning
                  ? `${importScanProgress?.fileCount || 0} files, ${importScanProgress?.folderCount || 0} folders discovered`
                  : `Status: ${importSession?.status || "idle"}`}
              </Typography>
              {!importScanning && importSession?.id && (
                <>
                  <LinearProgress variant="determinate" value={importProgress.percent} sx={{ mb: 1 }} />
                  <LinearProgress
                    variant="determinate"
                    value={importProgress.bytesPercent}
                    color="secondary"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" sx={{ color: C.textSec }}>
                    Files: {importSession.uploadedFiles || 0}/{importSession.totalFiles || 0} completed
                    {" • "}
                    Bytes: {formatBytes(importSession.uploadedBytes || 0)} / {formatBytes(importSession.totalBytes || 0)}
                    {" • "}
                    Failed: {importSession.failedFiles || 0}
                    {" • "}
                    Skipped: {importSession.skippedFiles || 0}
                  </Typography>
                </>
              )}
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!importScanning && ["ready", "uploading"].includes(importSession?.status) && (
                <Button size="small" variant="outlined" onClick={handlePauseImport}>
                  Pause
                </Button>
              )}
              {!importScanning && importSession?.status === "paused" && (
                <Button size="small" variant="contained" onClick={handleResumeImport}>
                  Resume
                </Button>
              )}
              {!importScanning && importFailedItems.length > 0 && (
                <Button size="small" variant="outlined" onClick={handleRetryFailedItems}>
                  Retry Failed
                </Button>
              )}
              {!importScanning && importSession?.id && (
                <Button size="small" variant="outlined" color="error" onClick={handleCancelImport}>
                  Cancel
                </Button>
              )}
              {!importScanning &&
                ["completed", "completed_with_errors", "cancelled"].includes(importSession?.status) && (
                  <Button size="small" variant="outlined" onClick={handleFinalizeImport}>
                    Refresh Results
                  </Button>
                )}
              <Button size="small" onClick={() => setImportMinimized(true)}>
                Minimize
              </Button>
            </Stack>
          </Stack>

          {!importScanning && importPendingFileAccess.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.25 }}>
              {importPendingFileAccess.length} file
              {importPendingFileAccess.length !== 1 ? "s" : ""} still need local file access. Use
              {" "}“Reselect folders” to continue after a refresh or browser restart.
            </Alert>
          )}

          {!importScanning && importActivity.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1.25 }}>
              {importActivity.map((entry) => (
                <Typography key={entry.id} variant="caption" sx={{ color: C.textSec }}>
                  {entry.fileName}: {entry.state}
                  {entry.detail ? ` (${entry.detail})` : ""}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {supportsImportSessions && importMinimized && (importScanning || importSession?.id) && (
        <Button
          size="small"
          variant="outlined"
          sx={{ alignSelf: "flex-start", mb: 1.25 }}
          onClick={() => setImportMinimized(false)}
        >
          Show Import Progress
        </Button>
      )}

      {dragOverlayActive && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            borderRadius: "12px",
            border: "2px dashed",
            borderColor: "primary.main",
            bgcolor: "rgba(15, 23, 42, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ textAlign: "center", bgcolor: "background.paper", px: 3, py: 2, borderRadius: "12px" }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {supportsImportSessions ? "Drop files and folders here" : "Drop files here"}
            </Typography>
            <Typography variant="body2" sx={{ color: C.textSec }}>
              {supportsImportSessions
                ? "Folder structure will be preserved"
                : "Files will upload into the current folder"}
            </Typography>
          </Box>
        </Box>
      )}

      <UITableShell
        customLayout
        paperSx={{
          flex: 1,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: "divider",
          borderTop: "3px solid",
          borderTopColor: "primary.main",
          minHeight: 0,
          mt: 0,
          boxShadow:
            "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <UITableToolbar sx={{ backgroundColor: C.paperBg, p: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ px: 2, py: 1, borderBottom: `1px solid ${C.border}` }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: C.textPri, lineHeight: 1 }}
              >
                Files
              </Typography>
              {filteredFiles.length > 0 && (
                <Chip
                  label={`${filteredFiles.length} file${
                    filteredFiles.length !== 1 ? "s" : ""
                  }`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    height: 22,
                    bgcolor: "#f1f5f9",
                    color: C.textSec,
                    border: `1px solid ${C.border}`,
                  }}
                />
              )}
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 20, alignSelf: "center" }}
              />

              {(() => {
                const allFolders = [
                  { folderName: "all", label: "All Files" },
                  ...folders
                    .filter(
                      (f) =>
                        String(f?.folderName || "").toLowerCase() !== "general",
                    )
                    .map((f) => ({
                      folderName: formatFolderValue(f),
                      label: f.relativePath || f.folderName,
                    })),
                ];
                const MAX_VISIBLE = 4;
                const activeIdx = allFolders.findIndex(
                  (f) =>
                    String(f.folderName).toLowerCase() ===
                    String(selectedFolder).toLowerCase(),
                );

                let visible = allFolders.slice(0, MAX_VISIBLE);
                const overflow = allFolders.slice(MAX_VISIBLE);
                const activeIsHidden = activeIdx >= MAX_VISIBLE;
                if (activeIsHidden) {
                  visible = [
                    ...visible.slice(0, MAX_VISIBLE - 1),
                    allFolders[activeIdx],
                  ];
                }
                return (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {visible.map(({ folderName, label }) => {
                      const isActive =
                        String(selectedFolder).toLowerCase() ===
                        String(folderName).toLowerCase();
                      return (
                        <Chip
                          key={folderName}
                          label={label}
                          size="small"
                          onClick={() => {
                            setSelectedFolder(folderName);
                            setTablePage(0);
                          }}
                          sx={{
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            height: 26,
                            bgcolor: isActive ? "primary.main" : "#f1f5f9",
                            color: isActive ? "#fff !important" : C.textSec,
                            border: `1px solid ${
                              isActive ? "transparent" : C.border
                            }`,
                            transition:
                              "background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
                            // "& .MuiChip-label": { px: "10px", color: isActive ? "#fff" : "inherit" },
                            // "&:hover": { bgcolor: isActive ? "primary.dark" : C.border },
                            "& .MuiChip-label": {
                              px: "10px",
                              color: isActive ? "#fff" : C.textSec, // ← THIS is what actually works
                            },
                            "&:hover": {
                              bgcolor: isActive ? "primary.dark" : C.border,
                            },
                          }}
                        />
                      );
                    })}
                    {overflow.length > 0 && (
                      <>
                        <Chip
                          label={`More (${overflow.length})`}
                          size="small"
                          onClick={(e) => setMoreFoldersAnchor(e.currentTarget)}
                          sx={{
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            height: 26,
                            bgcolor: "#f1f5f9",
                            color: C.textSec,
                            border: `1px solid ${C.border}`,
                            transition:
                              "background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
                            "&:hover": {
                              bgcolor: C.border,
                              boxShadow: "0 1px 4px rgba(15, 23, 42, 0.10)",
                            },
                          }}
                        />
                        <Menu
                          anchorEl={moreFoldersAnchor}
                          open={Boolean(moreFoldersAnchor)}
                          onClose={() => setMoreFoldersAnchor(null)}
                          slotProps={{
                            paper: { sx: { minWidth: 180, borderRadius: 2 } },
                          }}
                        >
                          {overflow.map(({ folderName, label }) => {
                            const isActive =
                              String(selectedFolder).toLowerCase() ===
                              String(folderName).toLowerCase();
                            return (
                              <MenuItem
                                key={folderName}
                                selected={isActive}
                                onClick={() => {
                                  setSelectedFolder(folderName);
                                  setTablePage(0);
                                  setMoreFoldersAnchor(null);
                                }}
                                sx={{
                                  fontSize: "0.875rem",
                                  fontWeight: isActive ? 700 : 400,
                                }}
                              >
                                <FolderOpenIcon
                                  sx={{
                                    fontSize: 16,
                                    mr: 1,
                                    color: isActive
                                      ? "primary.main"
                                      : C.textMuted,
                                  }}
                                />
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Menu>
                      </>
                    )}
                  </Stack>
                );
              })()}
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                size="small"
                onChange={(_, v) => {
                  if (v) setViewMode(v);
                }}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "1px solid",
                    borderColor: "divider",
                    px: 1,
                    py: 0.5,
                  },
                }}
              >
                {/* <ToggleButton value="table"><Tooltip title="Table view"><ViewListIcon sx={{ fontSize: 17 }} /></Tooltip></ToggleButton>
        <ToggleButton value="card"><Tooltip title="Card view"><ViewModuleIcon sx={{ fontSize: 17 }} /></Tooltip></ToggleButton> */}

                <ToggleButton value="card">
                  <Tooltip title="Card view">
                    <ViewModuleIcon sx={{ fontSize: 17 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="table">
                  <Tooltip title="Table view">
                    <ViewListIcon sx={{ fontSize: 17 }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              {viewMode === "table" && (
                <Tooltip title="Show / hide columns">
                  <IconButton
                    size="small"
                    onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                    sx={{ color: "text.secondary" }}
                  >
                    <ViewColumnIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {viewMode === "table" && (
                <Tooltip title="Row density">
                  <IconButton
                    size="small"
                    onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
                    sx={{ color: "text.secondary" }}
                  >
                    <FormatLineSpacingIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    size="small"
                    onClick={fetchFiles}
                    disabled={filesLoading}
                    sx={{ color: "text.secondary" }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Export">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                    disabled={!filteredFiles.length}
                    sx={{ color: "text.secondary" }}
                  >
                    <FileDownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 28, alignSelf: "center" }}
              />
              <Tooltip title="Create a new folder">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={
                    <CreateNewFolderIcon sx={{ fontSize: 15, color: "#fff" }} />
                  }
                  onClick={openFolderDialog}
                  sx={{
                    height: 32,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    boxShadow: "none",
                  }}
                >
                  New Folder
                </Button>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2,
              py: 0.75,
              bgcolor: "#f8fafc",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <TextField
              size="small"
              placeholder="Search files…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: C.textMuted }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setSearchQuery("")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box component="label" htmlFor="orderFilesUploadInput">
              <input
                id="orderFilesUploadInput"
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button
                component="span"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon sx={{ fontSize: 15 }} />}
                sx={{
                  height: 34,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedFiles.length
                  ? selectedFiles.length === 1
                    ? `${selectedFiles[0].name.slice(0, 14)}…`
                    : `${selectedFiles.length} files`
                  : "Choose File"}
              </Button>
            </Box>

            {supportsImportSessions && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon sx={{ fontSize: 15 }} />}
                onClick={handleImportFoldersClick}
                sx={{
                  height: 34,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                Import folders
              </Button>
            )}

            <Select
              size="small"
              value={selectedFolder}
              disabled={foldersLoading}
              onChange={(e) => setSelectedFolder(e.target.value)}
              renderValue={(val) => (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <FolderOpenIcon
                    sx={{ fontSize: 15, color: C.textMuted, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      color: C.textPri,
                      lineHeight: 1,
                    }}
                  >
                    {val === "all"
                      ? "All Files"
                      : val === "general"
                      ? "General Inbox (Legacy)"
                      : folders.find((f) => formatFolderValue(f) === val)?.relativePath ||
                        folders.find((f) => formatFolderValue(f) === val)?.folderName ||
                        val}
                  </Typography>
                </Stack>
              )}
              sx={{
                minWidth: 150,
                height: 34,
                bgcolor: "background.paper",
                borderRadius: "8px",
                fontSize: "0.8125rem",
                fontWeight: 500,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: "0 !important",
                  px: 1.25,
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "10px",
                    border: `1px solid ${C.border}`,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    mt: 0.5,
                    "& .MuiMenuItem-root": {
                      fontSize: "0.8125rem",
                      py: 0.75,
                      px: 1.5,
                      "&:hover": { bgcolor: "action.hover" },
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "#fff",
                        "&:hover": { bgcolor: "primary.dark" },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All Files</MenuItem>
              {folders
                .filter(
                  (f) =>
                    String(f?.folderName || "").toLowerCase() !== "general",
                )
                .map((f) => (
                  <MenuItem key={f.id} value={formatFolderValue(f)}>
                    {f.relativePath || f.folderName}
                  </MenuItem>
                ))}
            </Select>

            {selectedFiles.length > 0 && (
              <Button
                variant="contained"
                size="small"
                onClick={handleUpload}
                disabled={uploading}
                sx={{
                  height: 34,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "8px",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {uploading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  "Upload"
                )}
              </Button>
            )}

            {error && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", fontWeight: 600 }}
              >
                {error}
              </Typography>
            )}
          </Stack>
        </UITableToolbar>

        {filesLoading && filteredFiles.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, borderTop: `1px solid ${C.border}`, bgcolor: "rgba(248,250,252,0.85)" }}>
            <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 600 }}>
              Refreshing files…
            </Typography>
          </Box>
        )}

        {filesLoading && filteredFiles.length === 0
          ? viewMode === "table"
            ? renderSkeletonTable()
            : renderSkeletonCards()
          : filteredFiles.length === 0
          ? renderEmptyState()
          : viewMode === "table"
          ? renderTableView()
          : renderCardGrid()}

        {!filesLoading && filteredFiles.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 0.5,
              borderTop: `1px solid ${C.border}`,
              bgcolor: C.paperBg,
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" sx={{ color: C.textMuted }}>
              Showing{" "}
              {Math.min(paginationStart + 1, filteredFiles.length)}–
              {paginationEnd}{" "}
              of {filteredFiles.length} file
              {filteredFiles.length !== 1 ? "s" : ""}
            </Typography>
            <TablePagination
              component="div"
              count={filteredFiles.length}
              page={tablePage}
              onPageChange={(_, newPage) => setTablePage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setTablePage(0);
              }}
              rowsPerPageOptions={FILES_PER_PAGE_OPTIONS}
              sx={{
                "& .MuiTablePagination-toolbar": { minHeight: 40, pl: 0 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.78rem",
                  color: C.textSec,
                },
              }}
            />
          </Box>
        )}
      </UITableShell>

      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 210, borderRadius: 2 } } }}
      >
        <Typography
          variant="overline"
          sx={{
            px: 2,
            pt: 1.25,
            pb: 0.5,
            display: "block",
            color: "text.disabled",
          }}
        >
          Columns
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {FILE_COLUMNS.filter((c) => !c.locked).map((col) => (
          <MenuItem
            key={col.id}
            onClick={() => toggleColVisibility(col.id)}
            sx={{ py: 0.4 }}
          >
            <Checkbox
              size="small"
              checked={visibleColumns.has(col.id)}
              sx={{ p: 0.5, mr: 0.5 }}
            />
            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
              {col.label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={densityMenuAnchor}
        open={Boolean(densityMenuAnchor)}
        onClose={() => setDensityMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
      >
        <Typography
          variant="overline"
          sx={{
            px: 2,
            pt: 1.25,
            pb: 0.5,
            display: "block",
            color: "text.disabled",
          }}
        >
          Row Density
        </Typography>
        {["compact", "comfortable", "spacious"].map((d) => (
          <MenuItem
            key={d}
            onClick={() => {
              setDensity(d);
              setDensityMenuAnchor(null);
            }}
            sx={{ py: 0.75 }}
          >
            <Checkbox
              size="small"
              checked={density === d}
              sx={{ p: 0.5, mr: 0.5 }}
            />
            <ListItemText
              primaryTypographyProps={{
                fontSize: "0.875rem",
                textTransform: "capitalize",
              }}
            >
              {d}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 160, borderRadius: 2 } } }}
      >
        <Typography
          variant="overline"
          sx={{
            px: 2,
            pt: 1.25,
            pb: 0.5,
            display: "block",
            color: "text.disabled",
          }}
        >
          Export
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        <MenuItem onClick={handleExportCSV} sx={{ fontSize: "0.875rem" }}>
          Export CSV
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ fontSize: "0.875rem" }}>
          Export PDF
        </MenuItem>
      </Menu>

      <Dialog
        open={folderDialogOpen}
        onClose={closeFolderDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CreateNewFolderIcon sx={{ color: "primary.main", fontSize: 22 }} />
            <span>New Folder</span>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important" }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 0.75, color: C.textPri }}
            >
              Folder Name{" "}
              <Typography component="span" sx={{ color: "error.main" }}>
                *
              </Typography>
            </Typography>
            <TextField
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newFolderName.trim())
                  handleCreateFolder();
              }}
              fullWidth
              size="small"
              autoFocus
              placeholder="e.g. Artwork, Invoices, Proofs…"
              error={!!folderCreateError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: "background.paper",
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 0.75, color: C.textPri }}
            >
              Description
              <Typography
                component="span"
                variant="caption"
                sx={{ color: C.textMuted, ml: 0.75, fontWeight: 400 }}
              >
                (optional)
              </Typography>
            </Typography>
            <TextField
              value={newFolderDesc}
              onChange={(e) => setNewFolderDesc(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="What files go in this folder?"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: "background.paper",
                },
              }}
            />
          </Box>

          {folderCreateError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {folderCreateError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeFolderDialog}
            disabled={folderCreating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || folderCreating}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              borderRadius: "8px",
            }}
          >
            {folderCreating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Create Folder"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importReviewOpen}
        onClose={() => setImportReviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Review Folder Import</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {importError && <Alert severity="error">{importError}</Alert>}
            {importSummary && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`${importSummary.totalFiles} files`} size="small" />
                <Chip label={`${importSummary.totalFolders} folders`} size="small" />
                <Chip label={formatBytes(importSummary.totalBytes)} size="small" />
                <Chip
                  label={`${importSummary.duplicatePathCount || 0} duplicate paths`}
                  size="small"
                  color={importSummary.duplicatePathCount ? "warning" : "default"}
                />
                <Chip
                  label={`${importSummary.emptyFolders?.length || 0} empty folders`}
                  size="small"
                />
              </Stack>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Root-folder behavior
                </Typography>
                <RadioGroup
                  value={importOptions.rootMode}
                  onChange={(event) =>
                    setImportOptions((prev) => ({ ...prev, rootMode: event.target.value }))
                  }
                >
                  <FormControlLabel value="preserve" control={<Radio size="small" />} label="Preserve selected folders" />
                  <FormControlLabel value="contents_only" control={<Radio size="small" />} label="Upload folder contents only" />
                  <FormControlLabel value="flatten" control={<Radio size="small" />} label="Flatten all files" />
                </RadioGroup>
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Conflict policy
                </Typography>
                <RadioGroup
                  value={importOptions.conflictMode}
                  onChange={(event) =>
                    setImportOptions((prev) => ({ ...prev, conflictMode: event.target.value }))
                  }
                >
                  <FormControlLabel value="keep_both" control={<Radio size="small" />} label="Keep both" />
                  <FormControlLabel value="skip_duplicate" control={<Radio size="small" />} label="Skip duplicate" />
                  <FormControlLabel value="replace_existing" control={<Radio size="small" />} label="Replace existing" />
                  <FormControlLabel value="use_newest_modified" control={<Radio size="small" />} label="Use newest modified file" />
                  <FormControlLabel value="ask_conflicts" control={<Radio size="small" />} label="Ask for conflicts" />
                </RadioGroup>
              </FormControl>
            </Stack>

            <Box
              sx={{
                border: `1px solid ${C.border}`,
                borderRadius: "10px",
                maxHeight: 280,
                overflow: "auto",
                p: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 1 }}>
                Import preview
              </Typography>
              <Stack spacing={0.5}>
                {createManifestFromScan(importSelection)
                  .slice(0, 250)
                  .map((entry) => (
                    <Typography key={entry.clientEntryId} variant="caption" sx={{ color: C.textSec }}>
                      {entry.entryType === "folder" ? "Folder" : "File"}: {entry.relativePath}
                    </Typography>
                  ))}
                {createManifestFromScan(importSelection).length > 250 && (
                  <Typography variant="caption" sx={{ color: C.textMuted }}>
                    Showing the first 250 entries only. The full manifest will still be uploaded in batches.
                  </Typography>
                )}
              </Stack>
            </Box>

            <Typography variant="caption" sx={{ color: C.textMuted }}>
              Destination:{" "}
              {selectedFolder === "general"
                ? "General Inbox"
                : destinationFolderRecord?.relativePath ||
                  destinationFolderRecord?.folderName ||
                  selectedFolder}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportReviewOpen(false)} disabled={importStarting}>Cancel</Button>
          <Button variant="contained" onClick={handleStartImport} disabled={importStarting}>
            {importStarting ? <CircularProgress size={20} color="inherit" /> : "Start Import"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lightboxOpen} onClose={closeLightbox} maxWidth="lg">
        <DialogContent className="lightbox-dialog-content">
          <IconButton
            className="lightbox-close-button"
            onClick={closeLightbox}
            size="large"
          >
            <CloseIcon />
          </IconButton>
          {lightboxFile && (() => {
            const src = resolvePdfSource(lightboxFile) || lightboxFile?.filePath || resolveOriginalFileSrc(lightboxFile) || resolvePreviewImageSrc(lightboxFile);
            const ext = getExt(src);
            const isImg = looksLikeImage(src);
            const isPdf = isPdfUrl(src) || isPdfLike(lightboxFile);
if (isImg) {
  return (
    <img
      src={src}
      alt={lightboxFile.fileName || "File preview"}
      className="lightbox-image"
    />
  );
}
            if (isPdf) {
              return (
                <div style={{ padding: 24, textAlign: 'center' }}>
                  {src ? 
                    <PdfThumb
                      src={src}
                      width={640}
                      height={760}
                      qualityDpr={2}
                      fallback={<GenericPdfPlaceholder fileName={lightboxFile?.fileName || "PDF"} />}
                    />
                  : <GenericPdfPlaceholder fileName={lightboxFile?.fileName || 'PDF'} />}
                  {src ? <Button sx={{ mt: 2 }} variant="contained" onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}>Open PDF</Button> : null}
                </div>
              );
            }
            return (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <p>{lightboxFile?.fileName || 'File'}</p>
                <Button variant="contained" onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}>
                  Open {ext}
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog
        open={metaDialogOpen}
        onClose={closeMetaDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>File details</DialogTitle>
        <DialogContent className="file-meta-dialog">
          <TextField
            label="Folder"
            name="folder"
            value={metaForm.folder}
            onChange={handleMetaChange}
            fullWidth
            margin="dense"
            select
            SelectProps={{ native: true }}
          >
            {folders.map((f) => (
              <option key={f.id} value={formatFolderValue(f)}>
                {f.relativePath || f.folderName}
              </option>
            ))}
            <option value="unfiled">Unfiled (All Files)</option>
            <option value="general">General Inbox (Legacy)</option>
          </TextField>
          <TextField
            label="Tag"
            name="tag"
            value={metaForm.tag}
            onChange={handleMetaChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Status"
            name="status"
            value={metaForm.status}
            onChange={handleMetaChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Source"
            name="source"
            value={metaForm.source}
            onChange={handleMetaChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Note"
            name="note"
            value={metaForm.note}
            onChange={handleMetaChange}
            fullWidth
            multiline
            rows={3}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          {canDeleteCustomerFile(metaTarget, authState) ? (
            <Button
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              sx={{ mr: "auto" }}
            >
              Delete File
            </Button>
          ) : null}
          <Button onClick={closeMetaDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMeta}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={deletingFile ? undefined : () => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this file?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete “{metaTarget?.fileName || "this file"}”. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={deletingFile} onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletingFile}
            onClick={handleDeleteFile}
          >
            {deletingFile ? "Deleting…" : "Delete File"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderFiles;
